import { useCallback } from 'react';
import { useFieldArray } from 'react-hook-form';
import { type DragEndEvent } from '@dnd-kit/core';
import { nanoid } from 'nanoid';

import {
  MAX_ORDER_ITEMS,
  MIN_ORDER_ITEMS,
} from '../../../form/schema/quiz/QuizOrderDataSchema';
import { focusField } from '../../focus/focusUtils';
import { type BuildFormControl } from '../../type';
import { useQuizControl } from '../QuizControlProvider';

/**
 * 순서 맞추기 보기 목록 조작.
 *
 * `useMultipleOptions` 와 같은 구조지만 **섞기(handleShuffle)가 없다.**
 * 객관식은 보기 순서가 의미를 갖지 않아 저자가 섞을 수 있지만, 순서 맞추기는
 * 배열 순서 자체가 정답이라 섞으면 정답이 바뀐다. 풀이 화면의 랜덤 배열은
 * 클라가 풀이 진입 시 별도로 수행한다.
 */
export const useOrderItems = ({
  control,
  index,
}: {
  control: BuildFormControl;
  index: number;
}) => {
  const { index: quizIndex } = useQuizControl();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `quizList.${index}.orderData.items`,
    keyName: '_rhfId',
  });

  const handleAdd = useCallback(() => {
    if (fields.length >= MAX_ORDER_ITEMS) return;
    append({ id: nanoid(8), text: '', richText: '' });
    focusField(`quizList.${quizIndex}.orderData.items.${fields.length}.text`, {
      scroll: true,
      delay: 100,
    });
  }, [append, fields.length, quizIndex]);

  const handleRemove = useCallback(
    (itemIndex: number) => {
      if (fields.length <= MIN_ORDER_ITEMS) return;
      remove(itemIndex);
    },
    [fields.length, remove],
  );

  const handleMove = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = fields.findIndex(item => item.id === active.id);
      const newIndex = fields.findIndex(item => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // 배열 순서가 곧 정답 순서다 — 여기서 순서를 바꾸면 정답도 함께 바뀐다.
      move(oldIndex, newIndex);
    },
    [fields, move],
  );

  return { items: fields, handleAdd, handleRemove, handleMove };
};
